import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Quote, SupplierRate } from '../../types/quote.types';
import { getAllLookups } from '../../api/lookups';
import { Lookup } from '../../types/lookup.types';

interface ContractCreationModalProps {
  open: boolean;
  onClose: () => void;
  quote: Quote | null;
  selectedSupplier: SupplierRate | null;
  onCreateContract: (contractData: ContractFormData) => Promise<void>;
  loading?: boolean;
}

interface ContractFormData {
  startDate: Date;
  endDate: Date;
  accountNumber: string;
  notes: string;
  status: string;
}

const ContractCreationModal: React.FC<ContractCreationModalProps> = ({
  open,
  onClose,
  quote,
  selectedSupplier,
  onCreateContract,
  loading = false,
}) => {
  const [statusOptions, setStatusOptions] = useState<string[]>([]);
  const [formData, setFormData] = useState<ContractFormData>({
    startDate: new Date(),
    endDate: new Date(Date.now() + (12 * 30 * 24 * 60 * 60 * 1000)), // 12 months from now
    accountNumber: quote?.meterIdentifier || '',
    notes: '',
    status: '',
  });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load status options from admin panel
  const loadStatuses = useCallback(async () => {
    try {
      const allLookups = await getAllLookups();
      const statusLookups = allLookups.filter((lookup: Lookup) => 
        lookup.type === 'UtilityServiceStatus' && lookup.active
      );
      const statusValues = statusLookups.map((lookup: Lookup) => lookup.value);
      setStatusOptions(statusValues);
      // Set default to first status if available
      if (statusValues.length > 0 && !formData.status) {
        setFormData(prev => ({ ...prev, status: statusValues[0] }));
      }
    } catch (error) {
      console.error('Error loading status options:', error);
    }
  }, [formData.status]);

  useEffect(() => {
    loadStatuses();
  }, [loadStatuses]);

  // Update end date when contract period changes
  React.useEffect(() => {
    if (selectedSupplier && formData.startDate) {
      const endDate = new Date(formData.startDate);
      endDate.setMonth(endDate.getMonth() + selectedSupplier.contractPeriodMonths);
      setFormData(prev => ({ ...prev, endDate }));
    }
  }, [selectedSupplier, formData.startDate]);

  const handleSubmit = async () => {
    if (!quote || !selectedSupplier) return;

    setCreating(true);
    setError(null);

    try {
      await onCreateContract(formData);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create contract');
    } finally {
      setCreating(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount);
  };

  if (!quote || !selectedSupplier) {
    return null;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { minHeight: '600px' }
        }}
      >
        <DialogTitle>
          <Typography variant="h5" component="div">
            Create Contract
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Quote Ref: {quote.quoteRef} → New Contract
          </Typography>
        </DialogTitle>

        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Contract Summary */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ backgroundColor: 'primary.light' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Contract Summary
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        Client
                      </Typography>
                      <Typography variant="body1">
                        {quote.client?.companyName || `${quote.client?.firstName} ${quote.client?.lastName}`}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        Service Type
                      </Typography>
                      <Typography variant="body1">
                        {quote.meterType === 'MPAN' ? 'Electricity' : 'Gas'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        Supplier
                      </Typography>
                      <Typography variant="body1">
                        {selectedSupplier.supplierName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        Annual Value
                      </Typography>
                      <Typography variant="h6" color="primary">
                        {formatCurrency(selectedSupplier.annualExpenditure)}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Contract Details Form */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Contract Details
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <DatePicker
                label="Contract Start Date"
                value={formData.startDate}
                onChange={(date) => setFormData(prev => ({ ...prev, startDate: date || new Date() }))}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <DatePicker
                label="Contract End Date"
                value={formData.endDate}
                onChange={(date) => setFormData(prev => ({ ...prev, endDate: date || new Date() }))}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    helperText: `${selectedSupplier.contractPeriodMonths} month contract`,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Account Number"
                value={formData.accountNumber}
                onChange={(e) => setFormData(prev => ({ ...prev, accountNumber: e.target.value }))}
                helperText="Customer account number with the supplier"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Initial Status"
                value={formData.status}
                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                helperText="Select the initial contract status"
                required
              >
                {statusOptions.length === 0 ? (
                  <MenuItem value="">Loading statuses...</MenuItem>
                ) : (
                  statusOptions.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))
                )}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Contract Notes"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Add any additional notes about this contract..."
              />
            </Grid>

            {/* Rate Details */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Rate Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Standing Charge
                  </Typography>
                  <Typography variant="body1">
                    {selectedSupplier.standingCharge ? `${selectedSupplier.standingCharge}p/day` : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Day Unit Rate
                  </Typography>
                  <Typography variant="body1">
                    {selectedSupplier.dayUnitRate ? `${selectedSupplier.dayUnitRate}p/kWh` : 'N/A'}
                  </Typography>
                </Grid>
                {selectedSupplier.nightUnitRate && (
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Night Unit Rate
                    </Typography>
                    <Typography variant="body1">
                      {selectedSupplier.nightUnitRate}p/kWh
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Annual Usage
                  </Typography>
                  <Typography variant="body1">
                    {quote.meterType === 'MPAN' ? quote.eac : quote.aq} kWh
                  </Typography>
                </Grid>
              </Grid>
            </Grid>

            {/* Savings Summary */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ backgroundColor: 'success.light' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Expected Savings
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" color="text.secondary">
                        Annual Savings
                      </Typography>
                      <Typography variant="h5" color="success.dark">
                        {formatCurrency(
                          quote.currentSupplier.annualExpenditure - selectedSupplier.annualExpenditure
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" color="text.secondary">
                        Monthly Savings
                      </Typography>
                      <Typography variant="h5" color="success.dark">
                        {formatCurrency(
                          quote.currentSupplier.monthlyExpenditure - selectedSupplier.monthlyExpenditure
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" color="text.secondary">
                        Percentage Savings
                      </Typography>
                      <Typography variant="h5" color="success.dark">
                        {selectedSupplier.percentageDifference.toFixed(2)}%
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={onClose} disabled={creating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={creating}
            startIcon={creating ? <CircularProgress size={20} /> : null}
          >
            {creating ? 'Creating Contract...' : 'Create Contract'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default ContractCreationModal;
